import React, { useEffect, useState, useCallback } from "react";
import AdminPageLayout from "../components/AdminPageLayout";
import { useToast } from "../../common/toast/ToastContext";
import {
  fetchQuizzes, fetchQuizById,
  createQuiz, updateQuiz, deleteQuiz,
} from "./adminContentService";

const STATUSES = ["DRAFT", "ACTIVE", "INACTIVE"];
const Q_TYPES = ["MCQ", "TRUE_FALSE", "SHORT_ANSWER"];

const emptyOption = () => ({ optionText: "", isCorrect: false, sortOrder: 0 });
const emptyQuestion = () => ({ question: "", questionType: "MCQ", explanation: "", sortOrder: 0, options: [emptyOption(), emptyOption()] });
const emptyForm = { title: "", passScore: 70, timeLimitMinutes: "", status: "DRAFT", questions: [emptyQuestion()] };

const AdminQuizzesPage = () => {
  const { showToast } = useToast();
  const [view, setView] = useState("list");
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);

  const loadItems = useCallback(async () => {
    setLoading(true);
    const params = { page, size: 20 };
    if (search) params.search = search;
    if (statusFilter) params.status = statusFilter;
    const res = await fetchQuizzes(params);
    if (res.status === 200) {
      const d = res.data;
      setItems(Array.isArray(d) ? d : d.content ?? []);
      setTotalPages(Array.isArray(d) ? 1 : d.totalPages ?? 1);
    }
    setLoading(false);
  }, [page, search, statusFilter]);

  useEffect(() => { if (view === "list") loadItems(); }, [view, loadItems]);

  const openCreate = () => { setEditingId(null); setForm(emptyForm); setView("form"); };

  const openEdit = async (id) => {
    const res = await fetchQuizById(id);
    if (res.status === 200) {
      const d = res.data;
      setForm({
        title: d.title || "",
        passScore: d.passScore ?? 70,
        timeLimitMinutes: d.timeLimitMinutes || "",
        status: d.status || "DRAFT",
        questions: d.questions?.length ? d.questions : [emptyQuestion()],
      });
      setEditingId(id);
      setView("form");
    } else {
      showToast("Failed to load quiz", "error");
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this quiz?")) return;
    const res = await deleteQuiz(id);
    if (res.status === 200 || res.status === 204) { showToast("Deleted", "success"); loadItems(); }
    else showToast(res.message || "Delete failed", "error");
  };

  const handleSave = async () => {
    if (!form.title.trim()) { showToast("Title is required", "error"); return; }
    setSaving(true);
    const payload = {
      title: form.title.trim(),
      passScore: Number(form.passScore),
      timeLimitMinutes: form.timeLimitMinutes ? Number(form.timeLimitMinutes) : null,
      status: form.status,
      questions: form.questions.filter((q) => q.question.trim()).map((q, i) => ({
        question: q.question,
        questionType: q.questionType,
        explanation: q.explanation || "",
        sortOrder: i,
        options: q.options.filter((o) => o.optionText.trim()).map((o, j) => ({
          optionText: o.optionText,
          isCorrect: o.isCorrect,
          sortOrder: j,
        })),
      })),
    };
    const res = editingId ? await updateQuiz(editingId, payload) : await createQuiz(payload);
    setSaving(false);
    if (res.status === 200 || res.status === 201) {
      showToast(editingId ? "Updated" : "Created", "success");
      setView("list");
    } else {
      showToast(res.message || "Save failed", "error");
    }
  };

  const [bulkOpen, setBulkOpen] = useState(false);
  const [bulkText, setBulkText] = useState("");

  const setField = (key, val) => setForm((f) => ({ ...f, [key]: val }));

  const updateQuestion = (qi, field, val) =>
    setField("questions", form.questions.map((q, i) => i === qi ? { ...q, [field]: val } : q));

  const addQuestion = () => setField("questions", [...form.questions, emptyQuestion()]);
  const removeQuestion = (qi) => setField("questions", form.questions.filter((_, i) => i !== qi));

  // Bulk format per line:
  //   MCQ:          Question | CorrectAnswer | WrongA | WrongB | WrongC | Explanation
  //   TRUE_FALSE:   Question | True  (or False) | Explanation
  //   SHORT_ANSWER: Question | Explanation
  // Type prefix optional: "MCQ: ...", "TF: ...", "SA: ..."
  const handleBulkAdd = () => {
    const lines = bulkText.split("\n").map((l) => l.trim()).filter(Boolean);
    if (!lines.length) return;
    const parsed = lines.map((line) => {
      let type = "MCQ";
      let rest = line;
      if (/^MCQ:/i.test(line))  { type = "MCQ";          rest = line.replace(/^MCQ:/i, "").trim(); }
      else if (/^TF:/i.test(line))   { type = "TRUE_FALSE";   rest = line.replace(/^TF:/i, "").trim(); }
      else if (/^SA:/i.test(line))   { type = "SHORT_ANSWER"; rest = line.replace(/^SA:/i, "").trim(); }

      const parts = rest.split("|").map((p) => p.trim());

      if (type === "TRUE_FALSE") {
        const correctText = parts[1] || "True";
        const explanation = parts[2] || "";
        const isTrue = /^true$/i.test(correctText);
        return {
          question: parts[0], questionType: "TRUE_FALSE", explanation, sortOrder: 0,
          options: [
            { optionText: "True",  isCorrect: isTrue,  sortOrder: 0 },
            { optionText: "False", isCorrect: !isTrue, sortOrder: 1 },
          ],
        };
      }

      if (type === "SHORT_ANSWER") {
        return { question: parts[0], questionType: "SHORT_ANSWER", explanation: parts[1] || "", sortOrder: 0, options: [] };
      }

      // MCQ: parts[0]=question, parts[1]=correct, parts[2..n-1]=wrong, last=explanation if no wrong answers match
      const question = parts[0];
      const explanation = parts.length > 2 ? parts[parts.length - 1] : "";
      const optionParts = parts.slice(1, explanation ? parts.length - 1 : parts.length);
      const options = optionParts.map((text, j) => ({ optionText: text, isCorrect: j === 0, sortOrder: j }));
      if (options.length === 0) options.push({ optionText: "", isCorrect: true, sortOrder: 0 });
      return { question, questionType: "MCQ", explanation, sortOrder: 0, options };
    });
    setField("questions", [...form.questions.filter((q) => q.question.trim()), ...parsed]);
    setBulkText("");
    setBulkOpen(false);
  };

  const updateOption = (qi, oi, field, val) => {
    const qs = form.questions.map((q, i) => {
      if (i !== qi) return q;
      const opts = q.options.map((o, j) => {
        if (j !== oi) return field === "isCorrect" ? { ...o, isCorrect: false } : o;
        return { ...o, [field]: val };
      });
      return { ...q, options: opts };
    });
    setField("questions", qs);
  };

  const addOption = (qi) => {
    const qs = form.questions.map((q, i) => i === qi ? { ...q, options: [...q.options, emptyOption()] } : q);
    setField("questions", qs);
  };

  const removeOption = (qi, oi) => {
    const qs = form.questions.map((q, i) => i === qi ? { ...q, options: q.options.filter((_, j) => j !== oi) } : q);
    setField("questions", qs);
  };

  const setQuestionType = (qi, type) => {
    const defaultOpts = type === "TRUE_FALSE"
      ? [{ optionText: "True", isCorrect: false, sortOrder: 0 }, { optionText: "False", isCorrect: false, sortOrder: 1 }]
      : type === "SHORT_ANSWER" ? [] : [emptyOption(), emptyOption()];
    updateQuestion(qi, "questionType", type);
    const qs = form.questions.map((q, i) => i === qi ? { ...q, questionType: type, options: defaultOpts } : q);
    setField("questions", qs);
  };

  const inp = { width: "100%", padding: "8px 12px", borderRadius: 6, border: "1px solid #ccc", boxSizing: "border-box" };

  if (view === "form") {
    return (
      <div className="admin-container">
        <div className="admin-header">
          <h2>{editingId ? "Edit Quiz" : "New Quiz"}</h2>
          <div style={{ display: "flex", gap: 8 }}>
            <button onClick={() => setView("list")} style={{ padding: "8px 16px", borderRadius: 6, border: "1px solid #ccc", background: "#fff", cursor: "pointer" }}>Cancel</button>
            <button onClick={handleSave} disabled={saving} style={{ padding: "8px 20px", background: "#019095", color: "#fff", border: "none", borderRadius: 6, cursor: "pointer" }}>
              {saving ? "Saving…" : "Save"}
            </button>
          </div>
        </div>

        <div style={{ display: "flex", flexDirection: "column", gap: 16, maxWidth: 750 }}>
          <div>
            <label style={{ fontSize: 13, fontWeight: 600 }}>Title *</label>
            <input style={inp} value={form.title} onChange={(e) => setField("title", e.target.value)} placeholder="Quiz title" />
          </div>
          <div style={{ display: "flex", gap: 12 }}>
            <div style={{ flex: 1 }}>
              <label style={{ fontSize: 13, fontWeight: 600 }}>Pass Score (0–100)</label>
              <input type="number" min="0" max="100" style={inp} value={form.passScore} onChange={(e) => setField("passScore", e.target.value)} />
            </div>
            <div style={{ flex: 1 }}>
              <label style={{ fontSize: 13, fontWeight: 600 }}>Time Limit (minutes, optional)</label>
              <input type="number" min="1" style={inp} value={form.timeLimitMinutes} onChange={(e) => setField("timeLimitMinutes", e.target.value)} placeholder="Leave blank for no limit" />
            </div>
            <div style={{ flex: 1 }}>
              <label style={{ fontSize: 13, fontWeight: 600 }}>Status</label>
              <select style={inp} value={form.status} onChange={(e) => setField("status", e.target.value)}>
                {STATUSES.map((s) => <option key={s}>{s}</option>)}
              </select>
            </div>
          </div>

          <div>
            <label style={{ fontSize: 13, fontWeight: 600, display: "block", marginBottom: 8 }}>Questions</label>
            {form.questions.map((q, qi) => (
              <div key={qi} style={{ background: "#f9f9f9", borderRadius: 8, padding: 14, marginBottom: 14, border: "1px solid #e0e0e0" }}>
                <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 10 }}>
                  <span style={{ fontWeight: 600, fontSize: 13 }}>Question {qi + 1}</span>
                  <button onClick={() => removeQuestion(qi)} style={{ color: "#e53935", background: "none", border: "none", cursor: "pointer" }}>Remove</button>
                </div>
                <input style={{ ...inp, marginBottom: 8 }} value={q.question} onChange={(e) => updateQuestion(qi, "question", e.target.value)} placeholder="Question text *" />
                <div style={{ display: "flex", gap: 10, marginBottom: 10 }}>
                  <select style={{ ...inp, flex: 1 }} value={q.questionType} onChange={(e) => setQuestionType(qi, e.target.value)}>
                    {Q_TYPES.map((t) => <option key={t}>{t}</option>)}
                  </select>
                  <input style={{ ...inp, flex: 2 }} value={q.explanation} onChange={(e) => updateQuestion(qi, "explanation", e.target.value)} placeholder="Explanation (shown after answer)" />
                </div>

                {q.questionType !== "SHORT_ANSWER" && (
                  <div>
                    <p style={{ fontSize: 12, color: "#666", margin: "0 0 6px" }}>Options — check the correct one:</p>
                    {q.options.map((opt, oi) => (
                      <div key={oi} style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 6 }}>
                        <input type="radio" name={`correct-${qi}`} checked={opt.isCorrect}
                          onChange={() => updateOption(qi, oi, "isCorrect", true)}
                          title="Mark as correct" />
                        <input style={{ flex: 1, padding: "6px 10px", borderRadius: 6, border: "1px solid #ccc" }}
                          value={opt.optionText} onChange={(e) => updateOption(qi, oi, "optionText", e.target.value)}
                          placeholder={`Option ${oi + 1}`} />
                        {q.questionType === "MCQ" && (
                          <button onClick={() => removeOption(qi, oi)} style={{ color: "#e53935", background: "none", border: "none", cursor: "pointer" }}>✕</button>
                        )}
                      </div>
                    ))}
                    {q.questionType === "MCQ" && (
                      <button onClick={() => addOption(qi)} style={{ marginTop: 4, padding: "4px 12px", border: "1px dashed #019095", color: "#019095", background: "none", borderRadius: 6, cursor: "pointer", fontSize: 12 }}>+ Add Option</button>
                    )}
                  </div>
                )}
              </div>
            ))}
            <button onClick={addQuestion} style={{ padding: "6px 14px", borderRadius: 6, border: "1px dashed #019095", color: "#019095", background: "none", cursor: "pointer" }}>+ Add Question</button>

            {/* Bulk Add */}
            <div style={{ marginTop: 12 }}>
              <button
                onClick={() => setBulkOpen((o) => !o)}
                style={{ padding: "6px 14px", borderRadius: 6, border: "1px dashed #7c3aed", color: "#7c3aed", background: bulkOpen ? "#f5f3ff" : "none", cursor: "pointer", fontSize: 13, fontWeight: 600 }}
              >
                {bulkOpen ? "▲ Close Bulk Add" : "⚡ Bulk Add Questions"}
              </button>

              {bulkOpen && (
                <div style={{ marginTop: 10, background: "#f5f3ff", border: "1px solid #c4b5fd", borderRadius: 8, padding: 14 }}>
                  <p style={{ margin: "0 0 8px", fontSize: 13, color: "#555", lineHeight: 1.7 }}>
                    One question per line. Optional type prefix: <code style={{ background: "#ede9fe", padding: "1px 5px", borderRadius: 4 }}>MCQ:</code> <code style={{ background: "#ede9fe", padding: "1px 5px", borderRadius: 4 }}>TF:</code> <code style={{ background: "#ede9fe", padding: "1px 5px", borderRadius: 4 }}>SA:</code> (default: MCQ)<br />
                    <strong>MCQ:</strong> <code style={{ background: "#ede9fe", padding: "1px 5px", borderRadius: 4 }}>Question | CorrectOption | WrongA | WrongB | Explanation</code><br />
                    <strong>TF:</strong> <code style={{ background: "#ede9fe", padding: "1px 5px", borderRadius: 4 }}>TF: Question | True | Explanation</code><br />
                    <strong>SA:</strong> <code style={{ background: "#ede9fe", padding: "1px 5px", borderRadius: 4 }}>SA: Question | Explanation</code>
                  </p>
                  <textarea
                    autoFocus
                    value={bulkText}
                    onChange={(e) => setBulkText(e.target.value)}
                    placeholder={`Which of these is a proper noun? | London | city | river | country | London names a specific place\nTF: The sun rises in the east. | True | Basic geography\nSA: What is the plural of "child"? | The plural is children`}
                    style={{ width: "100%", minHeight: 160, padding: "10px 12px", borderRadius: 6, border: "1px solid #c4b5fd", fontSize: 13, resize: "vertical", boxSizing: "border-box", fontFamily: "monospace", lineHeight: 1.6 }}
                  />
                  <div style={{ display: "flex", gap: 8, marginTop: 8, alignItems: "center" }}>
                    <button
                      onClick={handleBulkAdd}
                      disabled={!bulkText.trim()}
                      style={{ padding: "7px 20px", background: "#7c3aed", color: "#fff", border: "none", borderRadius: 6, cursor: "pointer", fontWeight: 600, fontSize: 13, opacity: bulkText.trim() ? 1 : 0.5 }}
                    >
                      Add {bulkText.split("\n").filter((l) => l.trim()).length || 0} questions
                    </button>
                    <button onClick={() => { setBulkOpen(false); setBulkText(""); }}
                      style={{ padding: "7px 14px", border: "1px solid #ccc", background: "#fff", borderRadius: 6, cursor: "pointer", fontSize: 13 }}>
                      Cancel
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <AdminPageLayout
      title="Quizzes"
      subtitle="Manage quizzes linked to topics."
      actions={<button className="adm-btn adm-btn-primary" onClick={openCreate}>+ New Quiz</button>}
    >
      <div style={{ display: "flex", gap: 10, marginBottom: 20, flexWrap: "wrap" }}>
        <input placeholder="Search…" value={search} onChange={(e) => { setSearch(e.target.value); setPage(0); }}
          style={{ flex: 1, minWidth: 180, padding: "8px 12px", borderRadius: 6, border: "1px solid #ccc" }} />
        <select value={statusFilter} onChange={(e) => { setStatusFilter(e.target.value); setPage(0); }}
          style={{ padding: "8px 12px", borderRadius: 6, border: "1px solid #ccc" }}>
          <option value="">All Status</option>
          {STATUSES.map((s) => <option key={s}>{s}</option>)}
        </select>
      </div>

      {loading ? <p className="adm-loading">Loading…</p> : (
        <table className="announcement-table">
          <thead><tr><th>Title</th><th>Pass Score</th><th>Time Limit</th><th>Status</th><th>Questions</th><th>Actions</th></tr></thead>
          <tbody>
            {items.length === 0 ? (
              <tr><td colSpan="6">No quizzes found.</td></tr>
            ) : items.map((item) => (
              <tr key={item.id}>
                <td style={{ fontWeight: 600 }}>{item.title}</td>
                <td style={{ textAlign: "center" }}>{item.passScore}%</td>
                <td style={{ textAlign: "center" }}>{item.timeLimitMinutes ? `${item.timeLimitMinutes}m` : "—"}</td>
                <td><span style={{ padding: "2px 8px", borderRadius: 999, fontSize: 12, background: item.status === "ACTIVE" ? "#e8f5e9" : item.status === "DRAFT" ? "#fff8e1" : "#fce4ec", color: item.status === "ACTIVE" ? "#2e7d32" : item.status === "DRAFT" ? "#f57f17" : "#c62828" }}>{item.status}</span></td>
                <td style={{ textAlign: "center" }}>{item.questions?.length || 0}</td>
                <td>
                  <button onClick={() => openEdit(item.id)}>Edit</button>
                  <button className="delete-btn" onClick={() => handleDelete(item.id)}>Delete</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {totalPages > 1 && (
        <div style={{ marginTop: 16 }}>
          <button disabled={page === 0} onClick={() => setPage(page - 1)}>Prev</button>
          <span style={{ margin: "0 10px" }}>Page {page + 1} / {totalPages}</span>
          <button disabled={page + 1 >= totalPages} onClick={() => setPage(page + 1)}>Next</button>
        </div>
      )}
    </AdminPageLayout>
  );
};

export default AdminQuizzesPage;
